/**
 * DYRMA Climate WhatsApp Helper
 * Bypasses Meta Cloud API / WhatsApp Business API complexity by utilizing
 * standard deep-link protocol (wa.me) supported across mobile and desktop clients.
 */

export const DEFAULT_WHATSAPP_NUMBER = process.env.NEXT_PUBLIC_WHATSAPP_NUMBER || '355696725730';

/**
 * Sanitizes any phone number into international WhatsApp wa.me format
 * E.g.: '+355 69 367 3867' -> '355693673867'
 *       '069 367 3867'     -> '355693673867'
 *       '355693673867'     -> '355693673867'
 */
export function sanitizeWhatsAppNumber(rawPhone?: string | null): string {
  if (!rawPhone) return DEFAULT_WHATSAPP_NUMBER;
  let cleaned = rawPhone.replace(/\D/g, '');
  if (cleaned.startsWith('0') && cleaned.length === 10) {
    cleaned = '355' + cleaned.substring(1);
  }
  return cleaned || DEFAULT_WHATSAPP_NUMBER;
}

export interface ProductWhatsAppPayload {
  productName: string;
  sku: string;
  priceFormatted: string;
  quantity: number;
  includeInstallation: boolean;
  productUrl?: string;
  whatsappNumber?: string;
}

/**
 * Builds a formatted WhatsApp order / inquiry deep-link
 */
export function buildProductWhatsAppLink(payload: ProductWhatsAppPayload): string {
  const { 
    productName, 
    sku, 
    priceFormatted, 
    quantity, 
    includeInstallation, 
    productUrl, 
    whatsappNumber 
  } = payload;

  const phone = sanitizeWhatsAppNumber(whatsappNumber);

  const lines = [
    'Përshëndetje DYRMA Climate! 👋',
    '',
    'Jam i interesuar të porosis / marr informacion për këtë produkt:',
    `📦 *Produkti:* ${productName}`,
    `🏷️ *SKU:* ${sku}`,
    `💰 *Çmimi:* ${priceFormatted}`,
    `🔢 *Sasia:* ${quantity} copë`,
    `🛠️ *Montim profesional:* ${includeInstallation ? 'Po (+7,000 ALL)' : 'Jo (vetëm pajisja)'}`,
  ];

  if (productUrl) {
    lines.push(`🔗 *Linku:* ${productUrl}`);
  }

  lines.push('', 'A është i disponueshëm për dërgesë / montim të menjëhershëm? Faleminderit!');

  const fullText = lines.join('\n');
  return `https://wa.me/${phone}?text=${encodeURIComponent(fullText)}`;
}

/**
 * Builds a general support inquiry WhatsApp link
 */
export function buildGeneralWhatsAppLink(customMessage?: string, rawPhone?: string): string {
  const phone = sanitizeWhatsAppNumber(rawPhone);
  const defaultText = customMessage || 'Përshëndetje DYRMA Climate! Kërkoj asistencë për sistemet e kondicionimit dhe ngrohjes.';
  return `https://wa.me/${phone}?text=${encodeURIComponent(defaultText)}`;
}

export interface OrderWhatsAppItem {
  name: string;
  quantity: number;
  price: number | string;
  sku?: string;
}

export interface OrderWhatsAppPayload {
  orderNumber: string;
  fullName: string;
  phone: string;
  email?: string;
  city?: string;
  address?: string;
  paymentMethod?: string;
  items: OrderWhatsAppItem[];
  totalAmount: number | string;
  customerNotes?: string;
  whatsappNumber?: string;
}

const PAYMENT_METHOD_LABELS: Record<string, string> = {
  CASH_ON_DELIVERY: 'Para në Dorëzim (Cash on Delivery)',
  BANK_TRANSFER: 'Transfertë Bankare (BKT / Raiffeisen / Credins)',
  PAYMENT_ON_SITE: 'Tërheqje & Pagesë në Magazinë (Elbasan)',
};

/**
 * Builds a pre-filled WhatsApp deep link for an official order confirmation
 */
export function buildOrderWhatsAppLink(payload: OrderWhatsAppPayload): string {
  const phone = sanitizeWhatsAppNumber(payload.whatsappNumber);
  const paymentMethodLabel = payload.paymentMethod
    ? PAYMENT_METHOD_LABELS[payload.paymentMethod] || payload.paymentMethod
    : 'Para në Dorëzim';

  const lines: string[] = [
    'Përshëndetje DYRMA Climate! 👋',
    '',
    'Konfirmoj porosinë time zyrtare të sapo regjistruar në dyqanin tuaj online:',
    `📋 *Nr. Porosisë:* ${payload.orderNumber}`,
    `👤 *Klienti:* ${payload.fullName}`,
    `📞 *Tel:* ${payload.phone}`,
  ];

  if (payload.email) {
    lines.push(`📧 *Email:* ${payload.email}`);
  }

  if (payload.address || payload.city) {
    const loc = [payload.address, payload.city].filter(Boolean).join(', ');
    lines.push(`📍 *Adresa e Dorëzimit:* ${loc}`);
  }

  lines.push(`💳 *Mënyra e Pagesës:* ${paymentMethodLabel}`);

  if (payload.items && payload.items.length > 0) {
    lines.push('', '📦 *Artikujt e Porositur:*');
    payload.items.forEach((item) => {
      const priceFormatted =
        typeof item.price === 'number'
          ? `${item.price.toLocaleString('sq-AL')} ALL`
          : item.price;
      lines.push(`• ${item.name} (${item.quantity}x) — ${priceFormatted}`);
    });
  }

  const totalFormatted =
    typeof payload.totalAmount === 'number'
      ? `${payload.totalAmount.toLocaleString('sq-AL')} ALL`
      : payload.totalAmount;
  lines.push('', `💰 *Totali i Faturës:* ${totalFormatted}`);

  if (payload.customerNotes && payload.customerNotes.trim()) {
    lines.push(`📝 *Shënime:* ${payload.customerNotes.trim()}`);
  }

  lines.push(
    '',
    'Ju lutem më kontaktoni për konfirmimin përfundimtar, faturën dhe koordinimin e dërgesës/montimit. Faleminderit!'
  );

  const fullText = lines.join('\n');
  return `https://wa.me/${phone}?text=${encodeURIComponent(fullText)}`;
}

